import { useLayoutEffect, useRef, useState } from 'react'
import { GestureResponderEvent, StyleSheet, TouchableOpacity, View, ViewProps } from 'react-native'
import { Positionable } from 'zens-positionable'
import { fromEvent } from 'rxjs'
import { filter } from 'rxjs/operators'

import { Color } from '../../style'
import { Icon } from '../icon'

const SPACING_TOTAL = 13
const ROTATE_SIZE = 20
const REMOVE_SIZE = 18
const MOVE_BORDERWIDTH = 2

const styles = StyleSheet.create({
  container: {},
  move: {
    position: 'absolute',
  },
  handle: {
    borderStyle: 'dashed',
    borderColor: Color.premiumBlack,
  },
  rotate: {
    position: 'absolute',
  },
  hovered: {
    borderColor: Color.greyBlue,
  },
  remove: {
    position: 'absolute',
  },
})

interface Props extends ViewProps {
  refHandlers: Partial<Positionable['refHandlers']>
  isRotating?: boolean
  isDragging?: boolean
  isSelected?: boolean
  onHover?: (action: 'drag' | 'rotate' | 'remove' | undefined, value: boolean) => void
  onRemove?: (event: GestureResponderEvent) => void
  scale?: number
}

const Decorator = (props: Props) => {
  const { refHandlers, isRotating, isDragging, isSelected, onHover, onRemove, scale = 1, style, children } = props
  const [hover, setHover] = useState<'drag' | 'rotate' | 'remove'>()
  const refRemove = useRef<any>(null)
  useLayoutEffect(() => {
    const subscription = fromEvent(
      [refHandlers.nwRotate?.current, refHandlers.dnd?.current, ...(onRemove ? [refRemove.current] : [])],
      'mouseenter',
    )
      .pipe(filter((event) => (event as MouseEvent).buttons === 0))
      .subscribe((event) => {
        const target = (event as MouseEvent).target
        onHover?.(
          target === refHandlers.nwRotate?.current ? 'rotate' : target === refRemove.current ? 'remove' : 'drag',
          true,
        )
        setHover(target === refHandlers.nwRotate?.current ? 'rotate' : target === refRemove.current ? 'remove' : 'drag')
      })
    return () => subscription.unsubscribe()
  }, [])

  useLayoutEffect(() => {
    const subscription = fromEvent(
      [refHandlers.nwRotate?.current, refHandlers.dnd?.current, ...(onRemove ? [refRemove.current] : [])],
      'mouseleave',
    ).subscribe(() => {
      onHover?.(hover, false)
      setHover(undefined)
    })
    return () => subscription.unsubscribe()
  }, [hover])

  const containerStyle = [
    styles.container,
    {
      padding: -SPACING_TOTAL / scale,
    },
    style,
  ]

  const rotateStyle = [
    styles.rotate,
    {
      left: -SPACING_TOTAL / scale,
      top: -SPACING_TOTAL / scale,
      width: ROTATE_SIZE / scale,
      height: ROTATE_SIZE / scale,
    },
  ]

  const removeStyle = [
    styles.remove,
    {
      right: -SPACING_TOTAL / scale,
      top: -SPACING_TOTAL / scale,
      width: REMOVE_SIZE / scale,
      height: REMOVE_SIZE / scale,
    },
  ]

  const iconStyle = {
    width: ROTATE_SIZE / scale,
    height: ROTATE_SIZE / scale,
  }

  const borderWidth = MOVE_BORDERWIDTH / scale

  const moveStyle = [
    styles.move,
    {
      left: -borderWidth,
      top: -borderWidth,
      right: -borderWidth,
      bottom: -borderWidth,
      borderWidth: hover === 'drag' || isSelected || isDragging ? borderWidth : 0,
    },
  ]
  return (
    <View style={containerStyle} ref={refHandlers.container} testID="Decorator.Container">
      {children}
      <View
        style={[
          moveStyle,
          (hover === 'drag' || isSelected || isDragging) && styles.handle,
          ((hover === 'drag' && !isRotating) || isDragging) && styles.hovered,
        ]}
        ref={refHandlers.dnd}
        testID="Decorator.Drag"
      />
      <View style={rotateStyle} ref={refHandlers.nwRotate} testID="Decorator.Rotate">
        {(!!hover || isRotating) && !isDragging && (
          <Icon
            name="rotate"
            color={hover === 'rotate' || isRotating ? Color.greyBlue : Color.premiumBlack}
            style={iconStyle}
          />
        )}
      </View>
      {!!onRemove && (
        <TouchableOpacity style={removeStyle} ref={refRemove} testID="Decorator.Remove" onPress={onRemove}>
          {!!hover && !isRotating && !isDragging && (
            <Icon name="cross" color={hover === 'remove' ? Color.greyBlue : Color.premiumBlack} style={iconStyle} />
          )}
        </TouchableOpacity>
      )}
    </View>
  )
}

export default Decorator
